import React, { useEffect, useRef } from 'react';
import Chart from 'chart.js/auto';

const StyledLineChart = ({ filteredOrders, startDate, endDate, chartInstanceRef: externalRef }) => {
  const chartRef = useRef(null);
  const chartInstance = useRef(null); // this is our internal reference

  useEffect(() => {
    const ctx = chartRef.current.getContext('2d');

    const gradient = ctx.createLinearGradient(0, 0, 0, 450);
    gradient.addColorStop(0, 'rgba(100, 100, 150, 0.8)');
    gradient.addColorStop(0.5, 'rgba(100, 100, 150, 0.35)');
    gradient.addColorStop(1, 'rgba(100, 100, 150, 0.05)');

    const isSingleDay = startDate && endDate &&
      new Date(startDate).toDateString() === new Date(endDate).toDateString();

    const generateChartData = () => {
      const chartData = {};

      filteredOrders.forEach(order => {
        const date = new Date(order.timestamp);
        let key;

        if (isSingleDay) {
          let hours = date.getHours();
          const minutes = '00';
          const ampm = hours >= 12 ? 'PM' : 'AM';
          hours = hours % 12 || 12;
          key = `${hours}:${minutes} ${ampm}`;
        } else {
          const options = { month: 'short', day: 'numeric' };
          key = date.toLocaleDateString('en-US', options);
        }

        if (!chartData[key]) {
          chartData[key] = { name: key, transactions: 0, total: 0 };
        }

        chartData[key].transactions += 1;
        chartData[key].total += parseFloat(order.grandTotal || 0);
      });

      const sortedKeys = [];

      if (isSingleDay) {
        const startHour = 8;
        const endHour = Math.max(...filteredOrders.map(order => new Date(order.timestamp).getHours()), 15);

        for (let h = startHour; h <= endHour; h++) {
          const displayHour = h % 12 === 0 ? 12 : h % 12;
          const ampm = h < 12 ? 'AM' : 'PM';
          const label = `${displayHour}:00 ${ampm}`;

          sortedKeys.push(label);

          if (!chartData[label]) {
            chartData[label] = { name: label, transactions: 0, total: 0 };
          }
        }
      } else {
        Object.keys(chartData)
          .sort((a, b) => new Date(a) - new Date(b))
          .forEach(key => sortedKeys.push(key));
      }

      return {
        labels: sortedKeys,
        transactions: sortedKeys.map(key => chartData[key].transactions),
        totals: sortedKeys.map(key => chartData[key].total)
      };
    };

    const { labels, transactions, totals } = generateChartData();

    if (chartInstance.current) {
      chartInstance.current.destroy();
    }

    chartInstance.current = new Chart(ctx, {
      type: 'line',
      data: {
        labels,
        datasets: [
          {
            label: 'Total Sales',
            data: totals,
            backgroundColor: gradient,
            pointBackgroundColor: 'white',
            borderWidth: 1,
            borderColor: '#6e7a89',
            fill: true
          }
        ]
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        interaction: {
          mode: 'nearest',
          intersect: true
        },
        stacked: false,
        elements: {
          line: {
            tension: 0.4,
            borderJoinStyle: 'round'
          },
          point: {
            radius: 5,
            hoverRadius: 8,
            backgroundColor: 'white'
          }
        },
        plugins: {
          tooltip: {
            callbacks: {
              label: function (context) {
                const dataIndex = context.dataIndex;
                const total = totals[dataIndex];
                const transaction = transactions[dataIndex];
                return [
                  `Total Sales: $${total.toFixed(2)}`,
                  `Transactions: ${transaction}`
                ];
              },
              labelColor: function () {
                return {
                  borderColor: '#ffffff',
                  backgroundColor: '#ffffff',
                  borderWidth: 2
                };
              }
            }
          },
          legend: {
            display: true
          }
        },
        scales: {
          x: {
            title: {
              display: true,
              text: isSingleDay ? 'Hour of Day' : 'Date'
            }
          },
          y: {
            beginAtZero: true,
            title: {
              display: true,
              text: 'Total Sales'
            }
          }
        },
        animation: {
          easing: 'easeInOutQuad',
          duration: 520
        }
      }
    });

    // ✅ assign to parent if prop ref passed
    if (externalRef) {
      externalRef.current = chartInstance.current;
    }

    return () => {
      if (chartInstance.current) {
        chartInstance.current.destroy();
      }
    };
  }, [filteredOrders, startDate, endDate, externalRef]); // 🔁 externalRef added here

  return (
    <div className="line-chart w-full">
      <div className="aspect-ratio">
        <canvas ref={chartRef} />
      </div>
    </div>
  );
};

export default StyledLineChart;
